import { useState, useCallback, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { UploadFile, UploadProps } from 'antd';
import {
  uploadOutgoingCalls,
  UploadFormData,
  UploadResult,
  downloadTemplate,
  getOutboundTaskTypes,
  getOutboundFormTemplates,
  selectOutboundTaskTypes,
  selectOutboundTaskTypeLabels,
  selectOutboundFormTemplates,
  selectTemplateLoading,
  selectUploadStatus,
} from '../../../store/reducers/OutgoingCallsUploadSlice';
import {
  getTaskScenarioMeta,
  ScenarioGroup,
} from '../../../models/Task/scenarioMapping';
import { formatUploadDateTime } from './uploadDateTime';

const NO_CONTRACT_ERROR = 'NO_CONTRACT';

export interface UseOutgoingCallsUploadPopupReturn {
  // Scenario selection
  taskType: string | undefined;
  onTaskTypeChange: (value: string | undefined) => void;
  taskTypes: string[];
  taskTypeLabels: Record<string, string>;

  // Scenario-specific fields
  name: string;
  onNameChange: (value: string) => void;
  fromDate: dayjs.Dayjs | null;
  onFromDateChange: (value: dayjs.Dayjs | null) => void;
  fromTime: dayjs.Dayjs | null;
  onFromTimeChange: (value: dayjs.Dayjs | null) => void;
  toDate: dayjs.Dayjs | null;
  onToDateChange: (value: dayjs.Dayjs | null) => void;
  toTime: dayjs.Dayjs | null;
  onToTimeChange: (value: dayjs.Dayjs | null) => void;
  templateId: string | undefined;
  onTemplateChange: (value: string | undefined) => void;
  formTemplates: { id: string; name: string }[];

  // File
  fileList: UploadFile[];
  onFileChange: UploadProps['onChange'];

  // Actions
  handleOk: () => Promise<UploadResult>;
  handleReset: () => void;
  handleDownloadTemplate: () => Promise<string | undefined>;

  // State
  scenarioGroup: ScenarioGroup;
  hasNoContract: boolean;
  isLoading: boolean;
  isTemplateLoading: boolean;
}

export const useOutgoingCallsUploadPopup =
  (): UseOutgoingCallsUploadPopupReturn => {
    const dispatch = useAppDispatch();

    // Outbound task types from API
    const taskTypes = useAppSelector(selectOutboundTaskTypes);
    const taskTypeLabels = useAppSelector(selectOutboundTaskTypeLabels);
    const formTemplates = useAppSelector(selectOutboundFormTemplates);
    const uploadStatus = useAppSelector(selectUploadStatus);
    const isTemplateLoading = useAppSelector(selectTemplateLoading);

    // Scenario selection
    const [taskType, setTaskType] = useState<string | undefined>(undefined);

    // Campaign name (dispensarization, form)
    const [name, setName] = useState('');

    // Campaign start date/time (dispensarization, form)
    const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(null);
    const [fromTime, setFromTime] = useState<dayjs.Dayjs | null>(null);

    // Campaign end date/time (dispensarization only)
    const [toDate, setToDate] = useState<dayjs.Dayjs | null>(null);
    const [toTime, setToTime] = useState<dayjs.Dayjs | null>(null);

    // Form template (form only)
    const [templateId, setTemplateId] = useState<string | undefined>(undefined);

    // File
    const [fileList, setFileList] = useState<UploadFile[]>([]);

    // Determine scenario group from selected task type
    const scenarioGroup = useMemo<ScenarioGroup>(() => {
      if (!taskType) return 'unknown';
      return getTaskScenarioMeta(taskType).group;
    }, [taskType]);

    // All supported scenario groups have backend upload contracts.
    const hasNoContract = useMemo(() => {
      if (!taskType) return false;
      return scenarioGroup === 'unknown';
    }, [taskType, scenarioGroup]);

    // Load outbound task types and form templates on mount
    useEffect(() => {
      dispatch(getOutboundTaskTypes());
      dispatch(getOutboundFormTemplates());
    }, [dispatch]);

    // Reset form when task type changes
    useEffect(() => {
      setName('');
      setFromDate(null);
      setFromTime(null);
      setToDate(null);
      setToTime(null);
      setTemplateId(undefined);
      setFileList([]);
    }, [taskType]);

    const onTaskTypeChange = useCallback((value: string | undefined) => {
      setTaskType(value);
    }, []);

    const onNameChange = useCallback((value: string) => {
      setName(value);
    }, []);

    const onFromDateChange = useCallback((value: dayjs.Dayjs | null) => {
      setFromDate(value);
    }, []);

    const onFromTimeChange = useCallback((value: dayjs.Dayjs | null) => {
      setFromTime(value);
    }, []);

    const onToDateChange = useCallback((value: dayjs.Dayjs | null) => {
      setToDate(value);
    }, []);

    const onToTimeChange = useCallback((value: dayjs.Dayjs | null) => {
      setToTime(value);
    }, []);

    const onTemplateChange = useCallback((value: string | undefined) => {
      setTemplateId(value);
    }, []);

    const setFileValue = useCallback<NonNullable<UploadProps['onChange']>>(
      ({ fileList }) => {
        setFileList(fileList);
      },
      [],
    );

    const handleReset = useCallback(() => {
      setTaskType(undefined);
      setName('');
      setFromDate(null);
      setFromTime(null);
      setToDate(null);
      setToTime(null);
      setTemplateId(undefined);
      setFileList([]);
    }, []);

    const handleDownloadTemplate = useCallback(async (): Promise<
      string | undefined
    > => {
      if (!taskType) return undefined;
      try {
        const result = await dispatch(downloadTemplate(taskType)).unwrap();
        return result;
      } catch (error) {
        if (error === NO_CONTRACT_ERROR) {
          return undefined;
        }
        throw error;
      }
    }, [dispatch, taskType]);

    const handleOk = useCallback(async (): Promise<UploadResult> => {
      if (!taskType) throw new Error('No scenario selected');
      if (!fileList?.length || !fileList[0].originFileObj) {
        throw new Error('No file selected');
      }

      let from: string | undefined;
      let to: string | undefined;

      if (fromDate) {
        from = formatUploadDateTime(fromDate, fromTime);
      }

      if (toDate) {
        to = formatUploadDateTime(toDate, toTime);
      }

      const formData: UploadFormData = {
        file: new FormData(),
        name: name || undefined,
        from,
        to,
        templateId,
      };
      formData.file.append('file', fileList[0].originFileObj);

      const result = await dispatch(
        uploadOutgoingCalls({ formData, taskType }),
      ).unwrap();
      return result;
    }, [
      dispatch,
      taskType,
      fileList,
      name,
      fromDate,
      fromTime,
      toDate,
      toTime,
      templateId,
    ]);

    return {
      taskType,
      onTaskTypeChange,
      taskTypes,
      taskTypeLabels,
      name,
      onNameChange,
      fromDate,
      onFromDateChange,
      fromTime,
      onFromTimeChange,
      toDate,
      onToDateChange,
      toTime,
      onToTimeChange,
      templateId,
      onTemplateChange,
      formTemplates: formTemplates.map((t) => ({ id: t.id, name: t.name })),
      fileList,
      onFileChange: setFileValue,
      handleOk,
      handleReset,
      handleDownloadTemplate,
      scenarioGroup,
      hasNoContract,
      isLoading: uploadStatus.responseLoading,
      isTemplateLoading,
    };
  };
